/**
 * Shared transfer-bonus math, mirroring App\Support\TransferRatioCalculator.
 *
 * `bonus_ratio` stores the bonus as a fraction: 0.30 is a 30% bonus. Four
 * components each re-derived this inline and each carried the same guess —
 * values under 1.01 were treated as a fraction and anything else as an
 * already-computed multiplier. That put a cliff inside the legitimate range:
 * 1.00 read as a 100% bonus while 1.01 read as a 1% bonus, so a 110% promo
 * would have displayed as 10%. Production already stores 1.00 rows, i.e. it
 * sits right on that edge (MY1-2921).
 */

/**
 * Convert a stored bonus_ratio into a multiplier (0.3 => 1.3).
 * Returns null when the value is not a usable number.
 */
export function bonusMultiplier(bonusRatio) {
    const ratio =
        typeof bonusRatio === 'number' ? bonusRatio : parseFloat(bonusRatio);

    if (!Number.isFinite(ratio)) {
        return null;
    }

    return 1 + ratio;
}

/**
 * Whole-number bonus percentage for a stored bonus_ratio (0.3 => 30).
 * Returns null when the value is not a usable number.
 */
export function bonusPercent(bonusRatio) {
    const multiplier = bonusMultiplier(bonusRatio);

    return multiplier === null ? null : Math.round((multiplier - 1) * 100);
}

/**
 * Combine a base transfer rate with a bonus, mirroring
 * TransferRatioCalculator::effectiveMultiplier.
 *
 * A bonus scales the transfer — a 30% bonus means 30% more of the destination
 * currency — so a 2.0 partner with a 0.3 bonus transfers at 2.0 x 1.3 = 2.6.
 * This is what RewardsCC's own totalRatio field reports for each bonus.
 */
export function effectiveRate(baseRate, bonusRatio) {
    const multiplier = bonusMultiplier(bonusRatio);

    if (multiplier === null || multiplier <= 1) {
        return baseRate;
    }

    return baseRate * multiplier;
}

/**
 * Largest left-hand number an exact mixed pair may use, largest N an N:1 form
 * may use, and how far a pair may sit from the stored value. Mirrors the
 * constants in
 * App\Support\TransferRatioCalculator — change both together.
 */
const PAIR_CAP = 10;
const WHOLE_RATIO_CAP = 10000;
const EXACT_TOLERANCE = 1e-4;
const STORAGE_ROUNDING = 5e-5;
const SNAP_TOLERANCE = 0.03;

/**
 * Parse the shapes the API and the admin forms actually hand us — a number, a
 * numeric string, or a "1:X" / "A:B" display string — into a multiplier.
 * Returns null when there is nothing usable to render.
 */
function toMultiplier(ratio) {
    if (ratio === null || ratio === undefined || ratio === '') {
        return null;
    }

    if (typeof ratio === 'string' && ratio.includes(':')) {
        const [left, right] = ratio.split(':');
        const numerator = parseFloat(left);
        const denominator = parseFloat(right);

        if (
            !Number.isFinite(numerator) ||
            !Number.isFinite(denominator) ||
            numerator <= 0
        ) {
            return null;
        }

        return denominator / numerator;
    }

    const parsed = typeof ratio === 'number' ? ratio : parseFloat(ratio);

    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The whole-number pair that stands in for a sub-1 multiplier, or null when
 * none is readable enough to be worth it. See the PHP docblock for the two
 * tiers and why the loose one is limited to N:1 forms.
 */
function humanizedPair(multiplier) {
    if (!(multiplier > 0) || multiplier >= 1) {
        return null;
    }

    // Ascending, so the first hit is already in lowest terms.
    for (let left = 2; left <= PAIR_CAP; left++) {
        const right = Math.round(left * multiplier);

        if (
            right >= 1 &&
            Math.abs(right / left - multiplier) <= EXACT_TOLERANCE
        ) {
            return `${left}:${right}`;
        }
    }

    const whole = wholeRatioFor(multiplier);

    return whole === null ? null : `${whole}:1`;
}

/**
 * How "round" a whole number reads: two points per trailing zero, one for
 * ending in 5. Mirrors TransferRatioCalculator::roundness.
 */
function roundness(number) {
    let score = 0;
    let rest = number;

    while (rest > 0 && rest % 10 === 0) {
        score += 2;
        rest /= 10;
    }

    return score + (rest % 10 === 5 ? 1 : 0);
}

/**
 * The N for an N:1 form that stands in for the multiplier, or null: an exact N
 * first (225:1 stays 225:1), then the roundest N storage rounding can't tell
 * apart (0.0033 => 300:1), then the roundest within SNAP_TOLERANCE
 * (0.14 => 7:1). Mirrors TransferRatioCalculator::wholeRatioFor.
 */
function wholeRatioFor(multiplier) {
    const exact = Math.round(1 / multiplier);

    if (exact >= 2 && exact <= WHOLE_RATIO_CAP && Math.abs(exact * multiplier - 1) < 1e-9) {
        return exact;
    }

    return (
        roundestWholeRatioWithin(multiplier, STORAGE_ROUNDING) ??
        roundestWholeRatioWithin(multiplier, SNAP_TOLERANCE * multiplier)
    );
}

/**
 * The roundest N whose 1/N sits within `tolerance` of the multiplier, the
 * nearest breaking ties, or null. Mirrors
 * TransferRatioCalculator::roundestWholeRatioWithin.
 */
function roundestWholeRatioWithin(multiplier, tolerance) {
    const lowest = Math.max(2, Math.ceil(1 / (multiplier + tolerance)));
    const highest =
        multiplier > tolerance
            ? Math.min(WHOLE_RATIO_CAP, Math.floor(1 / (multiplier - tolerance)))
            : WHOLE_RATIO_CAP;
    const target = 1 / multiplier;
    let best = null;

    for (let candidate = lowest; candidate <= highest; candidate++) {
        if (Math.abs(1 / candidate - multiplier) > tolerance) {
            continue;
        }

        if (
            best === null ||
            roundness(candidate) > roundness(best) ||
            (roundness(candidate) === roundness(best) &&
                Math.abs(candidate - target) < Math.abs(best - target))
        ) {
            best = candidate;
        }
    }

    return best;
}

/**
 * Render a ratio as "1:X", trimming trailing zeros — the un-humanized form,
 * for values a whole-number pair must not stand in for. Empty and unusable
 * values fall back to 1:1, matching normalizeStoredRatio().
 */
export function plainRatioDisplay(ratio) {
    const multiplier = toMultiplier(ratio);

    if (multiplier === null || multiplier === 0) {
        return '1:1';
    }

    // Two decimals round anything under 0.1 into a different rate (200:1 is
    // 0.005, which showed as 1:0.01), so small values keep at least the four
    // decimals storage holds, and two significant digits so a bonus on a tiny
    // rate still differs. Mirrors TransferRatioCalculator::plainRatioDisplay.
    const decimals =
        Math.abs(multiplier) < 0.1
            ? Math.max(4, 1 - Math.floor(Math.log10(Math.abs(multiplier))))
            : 2;

    return `1:${parseFloat(multiplier.toFixed(decimals))}`;
}

/**
 * Render a ratio for display, mirroring TransferRatioCalculator::ratioDisplay.
 *
 * Sub-1 ratios mean the member gives up more than they receive, and "1:0.14"
 * is close to unreadable; where a whole-number pair says the same thing it is
 * used instead (7:1). Ratios of 1 or better keep the familiar "1:X" form.
 */
export function ratioDisplay(ratio) {
    const multiplier = toMultiplier(ratio);

    if (multiplier === null || multiplier === 0) {
        return '1:1';
    }

    return humanizedPair(multiplier) ?? plainRatioDisplay(multiplier);
}

/**
 * Render a base rate and its bonus-scaled counterpart as a matched pair, so
 * the struck-through comparison never mixes a humanized pair with a decimal.
 * Mirrors TransferRatioCalculator::ratioDisplayPair.
 */
export function ratioDisplayPair(baseRatio, effectiveRatio) {
    const base = toMultiplier(baseRatio);
    const effective = toMultiplier(effectiveRatio);

    if (
        base !== null &&
        effective !== null &&
        Math.abs(effective - base) > 1e-9
    ) {
        return {
            base: plainRatioDisplay(base),
            effective: plainRatioDisplay(effective),
        };
    }

    const display = ratioDisplay(base);

    return { base: display, effective: display };
}
